import _ from "lodash";
import { clone, map } from "ramda";
import {
  addComment,
  addUserToReadByForBriefspread,
  addUserToReadByForComments,
  getComments
} from "src/commentView/actions";
import { sortCommentsByAccount } from "src/commentView/helpers";
import { AccountComments, Comment } from "src/commentView/types";
import { ERRORS } from "src/constants";
import { SPREAD_STATUSES } from "src/documentList/types";
import { updateSpreadMetadata } from "src/metadata/actions";
import { reducerWithInitialState } from "typescript-fsa-reducers";
import {
  changeSpreadState,
  enterMappingMode,
  exitMappingMode,
  exportReport,
  loadSpread,
  mapUnmappedAccount,
  rejectSpread,
  remapAccount,
  tagAccount,
  uncacheSpread,
  undoRule,
  unlockSpread,
  unmapHiddenAccountRow,
  updateAccount,
  updateDocumentExceptionState
} from "./actions";
import { Spread } from "./types";

export enum STATES {
  ERROR = "error",
  INITIAL = "initial",
  LOADED = "loaded",
  LOADING = "loading",
  DELETED = "deleted",
  REJECTED = "rejected"
}

export interface CompletedSpread {
  spread: Spread;
  version: number;
  status: STATES.LOADED | STATES.LOADING;
  mappingNodeId: string | null;
  comments: Comment[] | null;
  accountComments: AccountComments | null;
}

export interface ErrorSpread {
  status: STATES.ERROR | STATES.DELETED | STATES.REJECTED;
  errorMsg: string;
  mappingNodeId: null;
  comments: Comment[] | null;
  accountComments: AccountComments | null;
  spread?: Spread;
}

export interface LoadingSpread {
  status: STATES.LOADING;
  mappingNodeId: null;
  comments: Comment[] | null;
  accountComments: AccountComments | null;
  spread?: Spread;
}

export interface InitialSpread {
  status: STATES.INITIAL;
  mappingNodeId: null;
  comments: Comment[] | null;
  accountComments: AccountComments | null;
  spread?: Spread;
}

const initialSpread: InitialSpread = {
  status: STATES.INITIAL,
  mappingNodeId: null,
  comments: null,
  accountComments: null
};

export type SpreadState =
  | CompletedSpread
  | ErrorSpread
  | LoadingSpread
  | InitialSpread;

const spreadReducer = reducerWithInitialState<Readonly<SpreadState>>(
  initialSpread
)
  .case(unlockSpread.async.started, () => initialSpread)
  .case(uncacheSpread.async.started, () => initialSpread)
  .case(updateDocumentExceptionState.async.started, (state, args) => {
    let spread;
    if (state.spread) {
      spread = state.spread;
      spread.exceptionLoading = true;
    }

    return {
      ...state,
      spread
    };
  })
  .case(
    updateDocumentExceptionState.async.done,
    (state, { result: { spread, version } }) => {
      if (state.spread && state.spread.groundPages) {
        spread.groundPages = state.spread.groundPages;
        spread.exceptionLoading = false;
      }

      return {
        ...state,
        status: STATES.LOADED,
        spread,
        version
      };
    }
  )
  .case(updateDocumentExceptionState.async.failed, (state, { error }) => {
    let spread;
    if (state.spread) {
      spread = state.spread;
      spread.exceptionLoading = true;
    }

    return {
      ...state,
      status: STATES.ERROR,
      errorMsg: error.message,
      mappingNodeId: null,
      spread
    };
  })
  .case(
    loadSpread.async.done,
    (state, { result: { spread, comments, version } }) => {
      const accountComments = sortCommentsByAccount(comments);
      return {
        ...state,
        status: STATES.LOADED,
        spread,
        comments,
        accountComments,
        version
      };
    }
  )
  .case(
    changeSpreadState.async.done,
    (state, { result: { newStatus, version } }) => {
      if (state.status === STATES.LOADED) {
        const tempSpread = { ...state.spread };
        tempSpread.status = newStatus ? newStatus : state.status;
        return {
          ...state,
          status: STATES.LOADED,
          spread: tempSpread,
          version
        };
      } else {
        return state;
      }
    }
  )
  .case(changeSpreadState.async.failed, (state, { error }) => ({
    ...state,
    status: STATES.ERROR,
    errorMsg: error.message,
    mappingNodeId: null
  }))
  .case(loadSpread.async.started, (state, __) => ({
    ...state,
    status: STATES.LOADING,
    mappingNodeId: null
  }))
  .case(loadSpread.async.failed, (state, { error }) => {
    const getStatus = () => {
      if (error.message === ERRORS.SPREAD_DELETED_MESSAGE) {
        return STATES.DELETED;
      } else if (error.message === ERRORS.SPREAD_REJECTED_MESSAGE) {
        return STATES.REJECTED;
      } else {
        return STATES.ERROR;
      }
    };
    const status = getStatus();
    return {
      status,
      errorMsg: error.message,
      mappingNodeId: null,
      comments: null,
      accountComments: null
    };
  })
  .case(updateAccount.async.done, (state, { result: { spread, version } }) => ({
    ...state,
    status: STATES.LOADED,
    spread,
    version
  }))
  .case(updateAccount.async.failed, (state, { error }) => ({
    ...state,
    status: STATES.ERROR,
    errorMsg: error.message,
    mappingNodeId: null
  }))
  .case(
    unmapHiddenAccountRow.async.done,
    (state, { result: { spread, version } }) => ({
      ...state,
      status: STATES.LOADED,
      spread,
      version
    })
  )
  .case(unmapHiddenAccountRow.async.failed, (state, { error }) => ({
    ...state,
    status: STATES.ERROR,
    errorMsg: error.message,
    mappingNodeId: null
  }))
  .case(enterMappingMode, (state, mappingNodeId) => {
    if (state.status === STATES.LOADED) {
      return { ...state, mappingNodeId };
    }
    return { ...state };
  })
  .case(exitMappingMode, state => {
    if (state.status === STATES.LOADED) {
      return { ...state, mappingNodeId: null };
    }
    return { ...state };
  })
  .case(remapAccount.async.done, (state, { result: { spread, version } }) => ({
    ...state,
    status: STATES.LOADED,
    spread,
    version
  }))
  .case(remapAccount.async.failed, (state, { error }) => ({
    ...state,
    status: STATES.ERROR,
    errorMsg: error.message,
    mappingNodeId: null
  }))
  .case(
    mapUnmappedAccount.async.done,
    (state, { result: { spread, version } }) => ({
      ...state,
      status: STATES.LOADED,
      spread,
      version
    })
  )
  .case(mapUnmappedAccount.async.failed, (state, { error }) => ({
    ...state,
    status: STATES.ERROR,
    errorMsg: error.message,
    mappingNodeId: null
  }))
  .case(undoRule.async.done, (state, { result: { spread, version } }) => ({
    ...state,
    status: STATES.LOADED,
    spread,
    version
  }))
  .case(undoRule.async.failed, (state, { error }) => ({
    ...state,
    status: STATES.ERROR,
    errorMsg: error.message,
    mappingNodeId: null
  }))
  .case(getComments.async.started, state => ({
    ...state
  }))
  .case(getComments.async.done, (state, { result: comments }) => {
    const accountComments = sortCommentsByAccount(comments);
    return {
      ...state,
      comments,
      accountComments
    };
  })
  .case(getComments.async.failed, state => ({
    ...state
  }))
  .case(addComment.async.done, (state, { result: { comment, version } }) => {
    if (state.status === STATES.LOADED) {
      const comments = clone(state.comments) || [];
      comments.push(comment);

      const accountComments = clone(state.accountComments) || {};

      if (comment.accountId) {
        if (accountComments[comment.accountId]) {
          accountComments[comment.accountId].push(comment);
        } else {
          accountComments[comment.accountId] = [comment];
        }
      }

      return {
        ...state,
        version,
        comments,
        accountComments
      };
    }
    return {
      ...state
    };
  })
  .case(
    addUserToReadByForBriefspread.async.done,
    (state, { result: { version } }) => {
      if (state.status === STATES.LOADED) {
        return {
          ...state,
          version
        };
      }
      return {
        ...state
      };
    }
  )
  .case(addUserToReadByForComments.async.done, (state, params) => {
    const comments = map(comment => {
      const found = _.find(params.result.comments, c => c.id === comment.id);

      if (found && params.result.result) {
        const readBy = comment.readBy || [];
        readBy.push(params.result.userId);
        comment.readBy = readBy;
      }

      return comment;
    }, state.comments || []);

    const accountComments = sortCommentsByAccount(comments);

    return {
      ...state,
      comments,
      accountComments
    };
  })
  .case(updateSpreadMetadata.async.done, (state, { result }) => {
    if (state.status === STATES.LOADED) {
      return {
        ...state,
        spread: { ...state.spread, ...result.metadata },
        version: result.version
      };
    }
    return {
      ...state
    };
  })
  .case(tagAccount.async.done, (state, { result: { spread, version } }) => {
    return {
      ...state,
      status: STATES.LOADED,
      spread,
      version
    };
  })
  .case(exportReport.async.done, state => {
    return {
      ...state
    };
  })
  .case(rejectSpread.async.done, (state, result) => {
    const spread = clone(state.spread);
    if (spread) {
      spread.status = SPREAD_STATUSES.REJECTED;

      return { ...state, spread };
    } else {
      return state;
    }
  });

export default spreadReducer;



// WEBPACK FOOTER //
// ./src/spread/model/spread/reducer.ts